import { DeviceStatus } from '@/api/deviceApi'
import type { AccessAction, FeatureKey, ResourceId } from '@/config/access-control'
import {
	ACCESS_DENIED_NO_PROJECT_ACCESS_REASON,
	ACCESS_DENIED_ORG_OWNER_ONLY_REASON,
	ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON,
} from '@/lib/searchSchemas'
import type { Permissions } from '@/rbac/grants'
import {
	type PlatformUser,
	getAccessibleTenants,
	getDefaultOrganizationId,
	hasOnlyNonUsableTenants,
	isPlatformUser,
	resolveDefaultTenant,
	resolveDefaultTenantForOrg,
	resolveNoProjectAccessOrgId,
} from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useQuotaStore } from '@/stores/quotaStore'
import { isConsoleDeployment, isDemoDeployment, isPlatformDeployment } from '@/utils/deployment'
import { isAdmin, isOwnerOfOrganization } from '@/utils/permissions'
import { redirect } from '@tanstack/react-router'

interface GuardCtx {
	isAuthenticated: boolean
}

interface GuardLocation {
	pathname: string
	search?: Record<string, unknown>
}

interface GuardOptions {
	location?: GuardLocation
}

/** Core authentication guard. Throws redirect to /login for unauthenticated users. */
export function authenticatedRouteGuard(context: GuardCtx, options?: GuardOptions) {
	if (!context.isAuthenticated) {
		const redirectPath = options?.location?.pathname || '/'
		throw redirect({
			to: '/login',
			search: {
				redirect: redirectPath,
				...(options?.location?.search || {}),
			},
		})
	}
}

/** Organization-level guard — must be org owner. */
export function organizationLevelRouteGuard(context: GuardCtx, orgId: string, options?: GuardOptions) {
	authenticatedRouteGuard(context, options)

	const user = useAuthStore.getState().user
	const isOwner = isOwnerOfOrganization(user, orgId)

	if (!isOwner) {
		const platformUser = user && isPlatformUser(user) ? user : null
		const targetProject = platformUser ? resolveDefaultTenantForOrg(platformUser, orgId) : null
		if (targetProject) {
			throw redirect({
				to: '/orgs/$orgId/projects/$tenantId',
				params: { orgId, tenantId: targetProject },
				replace: true,
			})
		}

		throw redirect({
			to: '/orgs/$orgId/access-denied',
			params: { orgId },
			search: { reason: ACCESS_DENIED_ORG_OWNER_ONLY_REASON },
			replace: true,
		})
	}
}

/** Project settings — PlatformUser project admins only (matches nav-user Project Settings entry). */
export function projectSettingsAdminRouteGuard(
	context: GuardCtx,
	params: { orgId: string; tenantId: string },
	options?: GuardOptions,
) {
	authenticatedRouteGuard(context, options)

	if (!isAdmin()) {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/access-denied',
			params: { orgId: params.orgId, tenantId: params.tenantId },
			search: { reason: ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON },
			replace: true,
		})
	}
}

/** Console-only guard — restricts route to Console deployments. */
export function consoleOnlyRouteGuard(context: GuardCtx, options?: GuardOptions) {
	authenticatedRouteGuard(context, options)

	if (isPlatformDeployment() && !isDemoDeployment()) {
		throw redirectToFirstOrg()
	}
}

/** Platform-only guard — restricts route to Platform deployments. */
export function platformOnlyRouteGuard(context: GuardCtx, options?: GuardOptions) {
	authenticatedRouteGuard(context, options)

	if (isConsoleDeployment()) {
		throw redirectToFirstOrg()
	}
}

/** Redirect helper shared across guards. Callers use `throw redirectToFirstOrg()`. */
export function redirectToFirstOrg() {
	const user = useAuthStore.getState().user
	const orgs = user?.organizations

	if (!orgs?.length) {
		if (isPlatformUser(user)) {
			const resolved = resolveDefaultTenant(user)
			if (resolved) {
				return redirect({
					to: '/orgs/$orgId/projects/$tenantId',
					params: { orgId: resolved.orgId, tenantId: resolved.tenantId },
					replace: true,
				})
			}
			if (!user.tenant_list?.length || hasOnlyNonUsableTenants(user)) {
				const orgId = resolveNoProjectAccessOrgId(user)
				if (orgId) {
					return redirect({
						to: '/orgs/$orgId/access-denied',
						params: { orgId },
						search: { reason: ACCESS_DENIED_NO_PROJECT_ACCESS_REASON },
						replace: true,
					})
				}
				return redirect({
					to: '/access-denied',
					search: { reason: ACCESS_DENIED_NO_PROJECT_ACCESS_REASON },
					replace: true,
				})
			}
		}
		return redirect({ to: '/welcome' })
	}

	// Honor the user's pinned default organization (personal user setting, legacy org marker as fallback).
	const platformUser = user && isPlatformUser(user) ? user : null
	let targetOrgId = orgs[0].id
	const defaultOrgId = getDefaultOrganizationId(user)
	if (defaultOrgId) {
		if (orgs.some(o => o.id === defaultOrgId)) {
			targetOrgId = defaultOrgId
		} else if (platformUser && resolveDefaultTenantForOrg(platformUser, defaultOrgId)) {
			// Pinned org isn't owned; honor it only when it resolves to an accessible project.
			targetOrgId = defaultOrgId
		}
	}

	if (isConsoleDeployment()) {
		return redirect({
			to: '/orgs/$orgId/billing/$type',
			params: { orgId: targetOrgId, type: 'plan-details' },
		})
	}

	const targetProject = platformUser ? resolveDefaultTenantForOrg(platformUser, targetOrgId) : null
	if (targetProject) {
		return redirect({
			to: '/orgs/$orgId/projects/$tenantId',
			params: { orgId: targetOrgId, tenantId: targetProject },
		})
	}

	return redirect({
		to: '/orgs/$orgId/inventory',
		params: { orgId: targetOrgId },
	})
}

export type ProjectFallbackTarget =
	| {
			to: '/orgs/$orgId/projects/$tenantId'
			params: { orgId: string; tenantId: string }
			search?: undefined
	  }
	| {
			to: '/orgs/$orgId/inventory/$status'
			params: { orgId: string; status: DeviceStatus }
			search?: undefined
	  }
	| {
			to: '/access-denied'
			params?: undefined
			search: { reason: typeof ACCESS_DENIED_NO_PROJECT_ACCESS_REASON }
	  }

/** Resolves fallback route and params for a user who does not have access to their current tenant/project. */
export function getProjectFallbackTarget(
	platformUser: PlatformUser,
	orgId: string,
	currentTenantId?: string,
): ProjectFallbackTarget {
	const isOwner = isOwnerOfOrganization(platformUser, orgId)
	const orgProjects = getAccessibleTenants(platformUser).filter(t => t.org_id === orgId)

	if (isOwner) {
		if (orgProjects.length > 0) {
			const nextTenant = orgProjects.find(t => t.id !== currentTenantId) || orgProjects[0]
			return {
				to: '/orgs/$orgId/projects/$tenantId',
				params: { orgId, tenantId: nextTenant.id },
			}
		}
		return {
			to: '/orgs/$orgId/inventory/$status',
			params: { orgId, status: DeviceStatus.All },
		}
	}

	const userOrgs = new Set<string>()
	if (platformUser.organizations) {
		for (const org of platformUser.organizations) {
			userOrgs.add(org.id)
		}
	}
	if (platformUser.tenant_list) {
		for (const tenant of platformUser.tenant_list) {
			if (tenant.org_id) {
				userOrgs.add(tenant.org_id)
			}
		}
	}

	if (orgProjects.length > 0) {
		return {
			to: '/orgs/$orgId/projects/$tenantId',
			params: { orgId, tenantId: orgProjects[0].id },
		}
	}

	const otherOrgs = Array.from(userOrgs).filter(id => id !== orgId)
	if (otherOrgs.length > 0) {
		const otherOrgId = otherOrgs[0]
		const targetProject = resolveDefaultTenantForOrg(platformUser, otherOrgId)
		if (targetProject) {
			return {
				to: '/orgs/$orgId/projects/$tenantId',
				params: { orgId: otherOrgId, tenantId: targetProject },
			}
		}
		return {
			to: '/orgs/$orgId/inventory/$status',
			params: { orgId: otherOrgId, status: DeviceStatus.Active },
		}
	}

	return {
		to: '/access-denied',
		search: { reason: ACCESS_DENIED_NO_PROJECT_ACCESS_REASON },
	}
}

export interface RequireAccessOpts {
	resource?: ResourceId
	/**
	 * The action being attempted. Defaults to `'view'` because route guards run
	 * on navigation — the user is reading the page. Pages that gate creation
	 * inline still use `<AccessControlled action='create'>` on their controls,
	 * so passing `'create'` here is rarely correct.
	 */
	action?: AccessAction
	permission?: keyof Permissions
	featureKey?: FeatureKey
	location?: GuardLocation
	orgId?: string
	tenantId?: string
	/**
	 * When true, plan-locked routes hard-redirect to the billing page. Default false —
	 * the norm is to render the locked page inline so users discover the feature exists.
	 */
	redirectOnPlanLock?: boolean
}

/** Loader helper: prime quota cache so first-render `useQuota` hits hot cache. */
export async function prefetchQuota(tenantId: string | undefined) {
	if (!tenantId) {
		return
	}
	try {
		await useQuotaStore.getState().fetchQuotaUsage(tenantId)
	} catch {
		// Non-fatal: hooks handle their own loading/error state.
	}
}
